import type { Allergen, Diet, FoodFlag, FoodGroup, FoodItem } from "./types";

/**
 * Corrections to the generated food table, kept outside it for the same reason exercise
 * corrections are: `foods.json` is rebuilt from INDB by a script, and anything written into it by
 * hand is gone the next time that runs. (ADR-017, ADR-025)
 *
 * **What is wrong with the source.** A slice of INDB's rows are on a raw-ingredient basis rather
 * than per 100 g of the finished dish. The pattern is deep frying: the recipe counts the whole
 * quantity of oil the dish was fried in, not the fraction the food absorbs, so a samosa comes out
 * at 621 kcal/100 g and 838 kcal each. The rows are internally consistent — the macros are
 * inflated in step — so no arithmetic check finds them.
 *
 * The one fault an arithmetic check does find is a different one: every recipe that calls for
 * stock by the cup was priced at stock-cube strength, so a consommé carries a cube's protein and
 * fat against a thin soup's energy. Those rows the generator drops (macros over 1.15× the kcal).
 *
 * The generator already drops anything over 1,000 kcal a serving. What is left here are the few
 * that survive that and can be replaced with a figure from a source that actually measured a
 * served portion, rather than one invented to look reasonable.
 */

/** Values below come from the portion reference in `docs/research/food-tracking.md`. */
export interface FoodOverride {
  /** kcal per 100 g of the dish as served */
  k: number;
  why: string;
}

export const FOOD_OVERRIDES: Readonly<Record<string, FoodOverride>> = {
  // Research: "1 samosa (medium): ~220–310 kcal". INDB has 814–842 each, which is a samosa's
  // worth of food plus the entire karahi of oil.
  ASC361: { k: 188, why: "potato samosa ~265 kcal for a 141 g medium samosa" },
  ASC362: { k: 215, why: "keema samosa ~290 kcal for a 135 g medium samosa" },
  ASC363: { k: 215, why: "paneer-pea samosa ~290 kcal for a 135 g medium samosa" },
};

/**
 * Deep-fried dishes whose INDB values are on the same raw-oil basis but which the reference gives
 * no measured portion for. Dropped rather than guessed at: a wrong number logged every week does
 * more damage than a missing row, which at least prompts a hand-typed entry.
 *
 * Matched on the name because the codes run to dozens of regional variants of each.
 */
export const FRIED_UNCORRECTED = [
  "poori",
  "puri",
  "vada",
  "vadai",
  "bhatura",
  "pakora",
  "pakoda",
  "bonda",
  "bhujia",
  "jalebi",
  "namakpara",
  "mathri",
  "chakli",
];

/**
 * Other words a food goes by, so the search finds it under the name a person actually types.
 * Keyed by table id. Search reads them as though they were part of the name. (lib/foodDb.ts)
 */
export const FOOD_ALIASES: Readonly<Record<string, readonly string[]>> = {
  L001: ["isabgol", "isabgul", "sat isabgol", "psyllium", "ispaghula", "fibre husk"],
  L002: ["kurkure", "masala munch", "namkeen"],
  U170554: ["chia", "sabja"],
  U169677: ["chips", "crisps", "wafers", "lays"],
};

/**
 * The words the commonest home foods go by in Hindi script and in Hinglish, so a search for
 * "रोटी" or "chawal" finds what "roti" and "rice" find. Display names stay English; these are
 * search words only. (plan §9 row 8)
 *
 * Keyed by what the English name says rather than by table id, like `FRIED_UNCORRECTED`: the words
 * name a family of dishes — "chawal" is every rice dish, not one row — and a rebuilt table that
 * renumbers or adds a rice dish still gets them. Each `en` is tested against the lowercased
 * English name; every row it matches carries `words`. Where a Hindi word has two common
 * spellings (with and without the nukta, अंडा and अण्डा) both are listed, because the phone's
 * keyboard decides which one arrives.
 *
 * Kadhi and jaggery are missing on purpose: neither table has a row for them, and an alias that
 * points at something else would be a confident wrong answer.
 */
export interface HomeWords {
  en: RegExp;
  words: readonly string[];
}

export const HOME_WORDS: readonly HomeWords[] = [
  // grains and breads
  { en: /\b(roti|chapati|chapathi|phulka)\b/, words: ["roti", "रोटी", "chapati", "chapatti", "चपाती", "phulka", "फुलका"] },
  { en: /\brice\b/, words: ["chawal", "chaawal", "chaval", "चावल", "bhaat", "bhat", "भात"] },
  { en: /\bparant?ha\b|\bparotta\b/, words: ["paratha", "parantha", "prantha", "पराठा", "परांठा"] },
  // Bhel, pani and sev puri are chaat, not the fried bread.
  { en: /(?<!(bhel|pani|sev|dahi) )\b(puri|poori)\b/, words: ["puri", "poori", "पूरी", "पूड़ी"] },
  { en: /\bnaan\b/, words: ["नान"] },
  { en: /\bpoha\b|\brice flakes\b|\b(flattened|beaten) rice\b/, words: ["poha", "pohe", "पोहा", "chiwda", "चिवड़ा"] },
  { en: /\bupma\b/, words: ["उपमा", "uppittu"] },
  { en: /\bidl[iy]\b/, words: ["इडली", "idly"] },
  { en: /\bdosai?\b/, words: ["डोसा", "dosai"] },
  { en: /\bkh?it?ch(d|r)i\b|\bkhichadi\b/, words: ["khichdi", "khichri", "khichadi", "खिचड़ी", "खिचडी"] },
  { en: /\bpula[ou]\b|\bpilaf\b/, words: ["pulao", "pulav", "पुलाव"] },
  { en: /\bbiri?yani\b/, words: ["biryani", "बिरयानी"] },
  { en: /\b(semolina|suji|sooji|rava|rawa)\b/, words: ["suji", "sooji", "rava", "सूजी", "रवा"] },
  { en: /\b(gram flour|chickpea flour|besan)\b/, words: ["besan", "बेसन"] },
  { en: /\bchill?a\b|\bcheela\b/, words: ["chilla", "cheela", "चीला"] },
  { en: /\bdhokla\b|\bkhaman\b/, words: ["dhokla", "ढोकला"] },
  // dals and beans
  { en: /\b(dal|dhal|daal|lentils?)\b/, words: ["dal", "daal", "dhal", "दाल"] },
  { en: /\b(moong|mung|green gram)\b/, words: ["moong", "mung", "मूंग", "मूँग"] },
  { en: /\b(masoor|red lentils?)\b/, words: ["masoor", "मसूर"] },
  { en: /\b(urad|black gram)\b/, words: ["urad", "urd", "उड़द", "उडद"] },
  { en: /\b(arhar|toor|tuvar|pigeon ?peas?|red gram)\b/, words: ["arhar", "toor", "tur", "अरहर", "तूर"] },
  { en: /\brajmah?\b|\bkidney beans?\b/, words: ["rajma", "rajmah", "राजमा"] },
  { en: /\bchickpeas?\b|\bchh?an+a\b|\bchh?ole\b/, words: ["chole", "chhole", "choley", "छोले", "chana", "चना"] },
  { en: /\bsambh?ar\b/, words: ["sambar", "sambhar", "सांबर", "सांभर"] },
  { en: /\bsprout(s|ed)?\b/, words: ["ankurit", "अंकुरित"] },
  { en: /\bsoya\b|\bsoyabean\b/, words: ["soya", "सोया"] },
  // vegetables
  { en: /\b(sabzi|sabji|subzi|subji|bhaji)\b|\bmixed vegetables?\b|\bvegetable curry\b/, words: ["sabzi", "sabji", "subzi", "सब्ज़ी", "सब्जी"] },
  { en: /\b(potato(es)?|aloo|alu)\b/, words: ["aloo", "alu", "आलू", "batata"] },
  { en: /\bcauliflower\b|\bphool ?gobh?i\b/, words: ["gobi", "gobhi", "phool gobi", "फूलगोभी", "गोभी"] },
  { en: /\bcabbage\b|\bpatta ?gobh?i\b/, words: ["patta gobi", "band gobhi", "पत्तागोभी", "बंदगोभी"] },
  { en: /\b(okra|bhindi)\b|\blady'?s ?fingers?\b/, words: ["bhindi", "bhendi", "भिंडी"] },
  { en: /\b(spinach|palak)\b/, words: ["palak", "पालक"] },
  { en: /\b(fenugreek|methi)\b/, words: ["methi", "मेथी"] },
  { en: /\bbottle ?gourd\b|\b(lauki|ghiya)\b/, words: ["lauki", "ghiya", "dudhi", "लौकी", "घीया"] },
  { en: /\bbitter ?gourd\b|\bkarel[ae]\b/, words: ["karela", "karele", "करेला"] },
  { en: /\b(brinjal|eggplant|aubergine|baingan)\b/, words: ["baingan", "baigan", "बैंगन"] },
  { en: /\b(peas|matar|mattar)\b|\bgreen pea\b/, words: ["matar", "mattar", "मटर"] },
  { en: /\b(carrots?|gajar)\b/, words: ["gajar", "गाजर"] },
  { en: /\b(radish(es)?|mooli)\b/, words: ["mooli", "मूली"] },
  { en: /\btomato(es)?\b/, words: ["tamatar", "टमाटर"] },
  { en: /\bonions?\b/, words: ["pyaz", "pyaaz", "kanda", "प्याज़", "प्याज"] },
  // dairy and eggs
  { en: /\b(curd|yogh?urt|dahi)\b/, words: ["dahi", "dahee", "दही"] },
  { en: /\bmilk\b/, words: ["doodh", "dudh", "दूध"] },
  { en: /\bpaneer\b/, words: ["paneer", "panir", "पनीर"] },
  { en: /\bghee\b|\bclarified butter\b/, words: ["ghee", "घी"] },
  { en: /\braita\b/, words: ["raita", "रायता"] },
  { en: /\blassi\b/, words: ["lassi", "लस्सी"] },
  { en: /\bbuttermilk\b|\bchaas\b/, words: ["chaas", "chhachh", "mattha", "छाछ", "मट्ठा"] },
  { en: /\beggs?\b/, words: ["anda", "ande", "unda", "अंडा", "अण्डा", "अंडे"] },
  // meat and fish
  { en: /\bchicken\b/, words: ["murgh", "murga", "मुर्गा", "मुर्ग", "चिकन"] },
  { en: /\b(mutton|lamb|goat)\b/, words: ["gosht", "मटन", "गोश्त"] },
  { en: /\bfish\b/, words: ["machli", "machhli", "मछली"] },
  // fruit, nuts, drinks, sweets and sides
  { en: /\bbananas?\b/, words: ["kela", "केला"] },
  { en: /\bapples?\b/, words: ["seb", "सेब"] },
  { en: /\bmangoe?s?\b/, words: ["aam", "आम"] },
  { en: /\b(peanuts?|groundnuts?)\b/, words: ["moongfali", "mungfali", "मूंगफली"] },
  { en: /\bcoconut\b/, words: ["nariyal", "नारियल"] },
  { en: /\bmakhana\b|\bfox ?nuts?\b|\blotus seeds?\b/, words: ["makhana", "मखाना"] },
  { en: /\btea\b|\bchai\b/, words: ["chai", "chay", "चाय"] },
  { en: /\bcoffee\b/, words: ["कॉफ़ी", "कॉफी"] },
  { en: /\bsugar\b/, words: ["cheeni", "chini", "shakkar", "चीनी", "शक्कर"] },
  { en: /\bhalwa\b|\bhalva\b/, words: ["halwa", "halva", "हलवा"] },
  { en: /\bkheer\b|\bpayasam\b/, words: ["kheer", "payasam", "खीर"] },
  { en: /\bladd?oo?s?\b|\bladdu\b/, words: ["ladoo", "laddu", "laddoo", "लड्डू"] },
  { en: /\bpapad\b|\bpapp?adum\b/, words: ["papad", "पापड़", "पापड"] },
  { en: /\bpickled?\b|\bachaa?r\b/, words: ["achar", "achaar", "अचार"] },
  { en: /\bchutney\b/, words: ["chutney", "चटनी"] },
];

/** The home words a row's English name earns, in list order, each once. */
export function homeWords(name: string): string[] {
  const n = name.toLowerCase();
  const out: string[] = [];
  for (const h of HOME_WORDS) {
    if (!h.en.test(n)) continue;
    for (const w of h.words) {
      const v = w.normalize("NFC");
      if (!out.includes(v)) out.push(v);
    }
  }
  return out;
}

/**
 * Tags the build's rules got wrong, by table id. A field given here replaces the generated one;
 * one left out keeps it. (ADR-038) The rules read a recipe's ingredients by weight, which is
 * right for a curry and wrong for the few dishes whose heaviest ingredient is not what they are.
 */
export interface TagOverride {
  gr?: FoodGroup;
  dt?: Diet;
  al?: Allergen[];
  fl?: FoodFlag[];
  why: string;
}

export const TAG_OVERRIDES: Readonly<Record<string, TagOverride>> = {
  BFP153: { gr: "grain", why: "appam is a rice pancake; the coconut outweighs the rice in the recipe" },
  OSR097: { gr: "other", fl: ["vegan", "jain"], why: "a dry spice blend, not a meal or something to pack" },
  ASC052: { gr: "dairy", why: "rice flakes served in milk, like the puffed rice beside it, not the fried chiwda" },
  BFP011: { gr: "drink", why: "a cup of hot chocolate is drunk; the chocolate made it read as a sweet" },
  ASC015: { gr: "drink", why: "cold coffee in a tall glass is drunk, ice cream or not" },
};

/** True when the row is one of the fried dishes we cannot price honestly. Only INDB's rows: the
 *  pricing error is in its recipes, and FNDDS's puri, vada and pakora are priced as eaten. */
export function isUncorrectedFried(item: FoodItem): boolean {
  if (item.src !== undefined && item.src !== "i") return false;
  const n = item.n.toLowerCase();
  // "Bhel puri" is not fried and must not be caught by "puri"; nor is "puri" inside "poorika".
  if (/bhel\s*puri|puri\s*rice/.test(n)) return false;
  return FRIED_UNCORRECTED.some((w) => new RegExp(`\\b${w}\\b`).test(n));
}

/**
 * Applies the corrections and removes what cannot be corrected.
 *
 * Runs once at load rather than in the generator so the generated file stays a faithful copy of
 * INDB and every departure from it is visible in one place.
 */
export function correct(foods: FoodItem[]): FoodItem[] {
  const out: FoodItem[] = [];
  for (const f of foods) {
    if (isUncorrectedFried(f)) continue;
    const fix = FOOD_OVERRIDES[f.i];
    // Only the energy is replaced. The error is frying oil, which carries no sodium, calcium,
    // iron or potassium, so the minerals (`na`, `ca`, `fe`, `pot`) ride through the spread as
    // the source gave them. (ADR-026)
    // A row's own aliases lead, so "isabgol" still starts the husk's alias string.
    const a = [...(FOOD_ALIASES[f.i] ?? []), ...homeWords(f.n)];
    const tags = TAG_OVERRIDES[f.i];
    let row = fix ? { ...f, k: fix.k } : f;
    if (tags) {
      const { why: _why, ...fields } = tags;
      row = { ...row, ...fields };
    }
    out.push(a.length ? { ...row, a: a.join(" ") } : row);
  }
  return out;
}
